import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import CloseIcon from '@mui/icons-material/Close';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import MinimizeIcon from '@mui/icons-material/Minimize';
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Collapse,
  IconButton,
  Link,
  Stack,
  type SxProps,
  type Theme,
  Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAccount } from '../../contexts/AccountContext';
import { apiService } from '../../services/api';
import { dismissUserAction } from '../../services/userService';
import { PERMISSIONS } from '../../shared/constants/permissions';
import type { UserAction } from '../../types';
import type { Tender } from '../../types/tender';
import { calculateDaysRemaining } from '../../utils/dateUtils';
import { calculateTenderStep } from '../../utils/tenderProgress';

const EMPTY_ARRAY: never[] = [];

const getPendingReviews = (tenders: Tender[]): Tender[] =>
  tenders.filter((t) => calculateTenderStep(t).step === 7);

const getUrgentTenders = (tenders: Tender[]): Tender[] =>
  tenders.filter((t) => {
    if ((t.status as string) === 'archived' || t.status === 'closed') {
      return false;
    }
    let days = t.daysUntilDeadline;
    if (days === undefined || days === null) {
      days = calculateDaysRemaining(t.submissionDeadline) ?? undefined;
    }
    return typeof days === 'number' && days >= 0 && days <= 3;
  });

interface PriorityActionBannerProps {
  tenders: Tender[];
  userActions?: UserAction[];
}

interface ActionItem {
  id: string;
  severity: 'info' | 'warning' | 'error' | 'success';
  title: string;
  message: React.ReactNode;
  actionLabel: string | null;
  onClick?: (() => void) | undefined;
  dismissible: boolean;
  onDismiss?: () => Promise<void>;
  style?: SxProps<Theme>;
}

const PriorityActionBanner = ({ tenders, userActions = [] }: PriorityActionBannerProps) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { account, refreshUserDetails, hasPermission } = useAccount();
  const [dismissedIds, setDismissedIds] = useState<string[]>([]);
  const [minimizedIds, setMinimizedIds] = useState<string[]>([]);

  useEffect(() => {
    if (account?.aiSettings?.dismissedDashboardActions) {
      setDismissedIds((prev) => {
        const serverIds = account.aiSettings?.dismissedDashboardActions || EMPTY_ARRAY;
        const combined = new Set([...prev, ...serverIds]);

        if (combined.size === prev.length && serverIds.every((id) => prev.includes(id))) {
          return prev;
        }

        return Array.from(combined);
      });
    }
  }, [account?.aiSettings?.dismissedDashboardActions]);

  const actions = useMemo(() => {
    const list: ActionItem[] = [];

    // 0. Dynamic User Actions
    for (const action of userActions) {
      if (action.status !== 'pending') {
        continue;
      }

      let actionTenderId = action.metadata?.tenderId || action.tenderId;
      if (!actionTenderId && action.link?.startsWith('/tender/')) {
        // Extract tenderId from link e.g. /tender/XYZ?step=...
        const match = action.link.match(/\/tender\/([^/?#]+)/);
        if (match) {
          actionTenderId = match[1];
        }
      }

      if (actionTenderId) {
        // Only show actions for tenders that exist in the active tenders list
        // Handle both exact match and composite IDs (e.g. user123_UL000001)
        const tenderExists = tenders.some(
          (t) => t.id === actionTenderId || t.id?.endsWith(`_${actionTenderId}`)
        );
        if (!tenderExists) {
          continue;
        }
      }

      const message = action.description;
      let style: SxProps<Theme> = {};

      if (action.title === 'Section Assigned') {
        const assignedBy = action.metadata?.assignedBy || 'System';
        const assignerTeam = action.metadata?.assignerTeam;

        // Handle Firestore Timestamp or Date object
        const createdAt = action.createdAt
          ? typeof action.createdAt === 'object' &&
            'toDate' in action.createdAt &&
            typeof (action.createdAt as { toDate: () => Date }).toDate === 'function'
            ? (action.createdAt as { toDate: () => Date }).toDate().toLocaleDateString()
            : new Date(action.createdAt as unknown as string | number | Date).toLocaleDateString()
          : 'Unknown Date';

        const customTitle = `Revisions/Review requested by ${assignedBy}${assignerTeam ? ` (${assignerTeam})` : ''} on ${createdAt}`;

        // Rust border for section assignments
        style = {
          border: `2px solid ${theme.palette.bidlogiq.rust}`,
        };

        list.push({
          id: action.id || 'unknown',
          severity: 'info',
          title: customTitle,
          message: action.description, // Keep original description (e.g. "You have been assigned...")
          actionLabel: 'View',
          onClick: () => action.link && navigate(action.link),
          dismissible: action.dismissible || false,
          style,
          onDismiss: async () => {
            if (!account?.uid || !action.id) {
              return;
            }
            try {
              await dismissUserAction(account.uid, action.id);
            } catch (_error) {
              // Ignore error
            }
          },
        });
        continue; // Skip the standard push
      }

      list.push({
        id: action.id || 'unknown',
        severity: 'info',
        title: action.title,
        message: message,
        actionLabel: 'View',
        onClick: () => action.link && navigate(action.link),
        dismissible: true,
        style, // Pass custom style
        onDismiss: async () => {
          if (!account?.uid || !action.id) {
            return;
          }
          try {
            await dismissUserAction(account.uid, action.id);
          } catch (_error) {
            // Ignore error
          }
        },
      });
    }

    // 1. Critical Onboarding: Company Information
    // This is mandatory and cannot be dismissed until completed.
    if (account && !account.company_registration) {
      list.push({
        id: 'onboarding-company-info',
        severity: 'error' as const,
        title: 'Setup Required: Company Information',
        message: 'You must fill in your company details to complete the sign-up process.',
        actionLabel: 'Complete Setup',
        onClick: () => navigate('/company-info'),
        dismissible: false,
      });
    }

    // SharePoint Expiry Warning
    if (account?.storageType === 'sharepoint' && account.sharepointConfig?.secretExpiryDate) {
      const expiryDate = new Date(account.sharepointConfig.secretExpiryDate as string);
      const today = new Date();
      const diffTime = expiryDate.getTime() - today.getTime();
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

      if (diffDays <= 21) {
        list.push({
          id: 'sharepoint-secret-expiry',
          severity: diffDays <= 7 ? 'error' : 'warning',
          title:
            diffDays <= 0
              ? 'Action Required: SharePoint Connection Expired'
              : `Action Required: SharePoint Connection Expires in ${diffDays} days`,
          message:
            'Your Azure AD Client Secret used to connect to SharePoint is expiring soon. To avoid interruptions in document generation and uploads, please generate a new secret in the Azure Portal and update it here.',
          actionLabel: 'Update Secret',
          onClick: () => navigate('/admin?tab=storage'),
          dismissible: false,
        });
      }
    }

    // 3. Onboarding: Security (MFA)
    if (!dismissedIds.includes('onboarding-mfa')) {
      list.push({
        id: 'onboarding-mfa',
        severity: 'info' as const,
        title: 'Secure your account (recommended)',
        message: 'Enable Multi-Factor Authentication (MFA) to keep your account secure.',
        actionLabel: 'Go to Account',
        onClick: () => navigate('/account'),
        dismissible: true,
      });
    }

    // 4. Onboarding: Storage (SharePoint)
    if (
      !dismissedIds.includes('onboarding-storage') &&
      (!account?.sharepointConfig || Object.keys(account.sharepointConfig).length === 0)
    ) {
      list.push({
        id: 'onboarding-storage',
        severity: 'info' as const,
        title: 'Connect Storage (optional)',
        message: 'Connect your own SharePoint site to keep your files in your own tenant.',
        actionLabel: 'Connect',
        onClick: () => {
          const isMultiUser = (account?.clientUserCount || 0) > 1;
          const canManageSettings = hasPermission(PERMISSIONS.SETTINGS_MANAGE);

          if (isMultiUser && canManageSettings) {
            navigate('/admin?tab=storage');
          } else {
            navigate('/account');
          }
        },
        dismissible: true,
      });
    }

    // 5. Onboarding: Library (Admins Only)
    const canManageLibrary = hasPermission('library.manage.client');
    if (canManageLibrary && !dismissedIds.includes('onboarding-library')) {
      let isMissingSharepointConnection = false;
      if (account?.storageType === 'sharepoint') {
        const archetypes = [
          'cv',
          'past_proposals',
          'case_studies',
          'procurement_knowledge',
          'proposal',
        ];
        isMissingSharepointConnection = archetypes.some((archetype) => {
          const llmSetting = (account?.llm_settings as Record<string, unknown>)?.[archetype] as {
            external_site?: { siteId?: string; driveId?: string };
            discovery?: { target_folder_paths?: string[] };
          };
          return !(
            llmSetting?.external_site?.siteId &&
            llmSetting?.external_site?.driveId &&
            Array.isArray(llmSetting?.discovery?.target_folder_paths) &&
            llmSetting.discovery.target_folder_paths.length > 0
          );
        });
      }

      list.push({
        id: 'onboarding-library',
        severity: 'error' as const,
        title: isMissingSharepointConnection
          ? 'Action Required: Connect Knowledge Base Folders'
          : 'Build your Knowledge Base (required for best outcomes)',
        message: isMissingSharepointConnection
          ? 'You must connect your SharePoint folders to the Knowledge Base so the AI can securely access your CVs, case studies, and past proposals.'
          : 'Upload your CVs, case studies, and past proposals to the Knowledge Base to improve AI generation quality.',
        actionLabel: 'Go to Knowledge Base',
        onClick: () => navigate('/admin/knowledge_base'),
        dismissible: true,
      });
    }

    // 6. Onboarding: Templates
    if (!dismissedIds.includes('onboarding-templates')) {
      list.push({
        id: 'onboarding-templates',
        severity: 'info' as const,
        title: 'Create Templates (recommended)',
        message:
          'Set up templates for your proposals, CVs, and case studies to ensure consistent formatting on your company letterhead.',
        actionLabel: 'Manage Templates',
        onClick: () => navigate('/templates'),
        dismissible: true,
      });
    }

    // Check for Pending Reviews (Step 7)
    const pendingReviews = getPendingReviews(tenders);
    if (pendingReviews.length > 0) {
      list.push({
        id: 'reviews',
        severity: 'warning' as const,
        title: 'Drafts are ready for revisions and reviews',
        message: (
          <Box>
            <Typography variant="body2">
              You have {pendingReviews.length} tender(s) ready for review.
            </Typography>
            <Stack direction="column" spacing={0.5} sx={{ mt: 1, pl: 1 }}>
              {pendingReviews.map((t) => (
                <Link
                  key={t.id}
                  component="button"
                  variant="body2"
                  onClick={() => navigate(`/tender/${t.id}?step=7`)}
                  sx={{
                    color: 'inherit',
                    textDecoration: 'underline',
                    textAlign: 'left',
                    fontWeight: 'medium',
                    width: 'fit-content',
                    '&:hover': { opacity: 0.8 },
                  }}
                >
                  • {t.title || 'Untitled Tender'}
                </Link>
              ))}
            </Stack>
          </Box>
        ),
        actionLabel: null,
        onClick: undefined,
        dismissible: true,
      });
    }

    // Check for Urgent Deadlines (Active and < 3 days)
    const urgent = getUrgentTenders(tenders);
    if (urgent.length > 0) {
      list.push({
        id: 'urgent',
        severity: 'error' as const,
        title: 'Urgent: Deadlines Approaching',
        message: (
          <Box>
            <Typography variant="body2">
              {urgent.length} tender(s) represent immediate deadlines (&lt; 3 days).
            </Typography>
            <Stack direction="column" spacing={0.5} sx={{ mt: 1, pl: 1 }}>
              {urgent.map((t) => {
                const days = t.daysUntilDeadline ?? calculateDaysRemaining(t.submissionDeadline);
                return (
                  <Link
                    key={t.id}
                    component="button"
                    variant="body2"
                    onClick={() => navigate(`/tender/${t.id}`)}
                    sx={{
                      color: 'inherit',
                      textDecoration: 'underline',
                      textAlign: 'left',
                      fontWeight: 'medium',
                      width: 'fit-content',
                      '&:hover': { opacity: 0.8 },
                    }}
                  >
                    • {t.title || 'Untitled Tender'} ({days} {days === 1 ? 'day' : 'days'})
                  </Link>
                );
              })}
            </Stack>
          </Box>
        ),
        actionLabel: null,
        onClick: undefined,
        dismissible: true,
      });
    }

    return list;
  }, [tenders, navigate, account, dismissedIds, userActions, theme, hasPermission]);

  const handleClose = async (id: string) => {
    // Optimistic hide for all action types
    const newDismissedIds = [...dismissedIds, id];
    setDismissedIds(newDismissedIds);

    const action = actions.find((a) => a.id === id);

    if (action?.onDismiss) {
      await action.onDismiss();
      return;
    }

    if (account) {
      try {
        await apiService.updateAccountDetails({
          aiSettings: {
            ...account.aiSettings,
            dismissedDashboardActions: newDismissedIds,
          },
        });
        refreshUserDetails();
      } catch {
        // Silent failure for saving dismissed actions
      }
    }
  };

  const handleMinimize = (id: string) => {
    setMinimizedIds((prev) =>
      prev.includes(id) ? prev.filter((mid) => mid !== id) : [...prev, id]
    );
  };

  const visibleActions = actions.filter((action) => !dismissedIds.includes(action.id));

  // If the user has disabled the action cards in settings, return null
  if (account?.aiSettings?.showDashboardActionCards === false) {
    return null;
  }

  if (visibleActions.length === 0) {
    return null;
  }

  return (
    <Box sx={{ mb: 3 }}>
      <Stack spacing={2}>
        {actions.map((action) => {
          const isMinimized = minimizedIds.includes(action.id);

          if (dismissedIds.includes(action.id)) {
            return null;
          }

          return (
            <Collapse in={!dismissedIds.includes(action.id)} key={action.id} unmountOnExit>
              <Alert
                severity={action.severity}
                action={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    {!isMinimized && action.actionLabel && (
                      <Button
                        color="inherit"
                        size="small"
                        endIcon={<ArrowForwardIcon />}
                        onClick={action.onClick}
                        sx={{ mr: 1 }}
                      >
                        {action.actionLabel}
                      </Button>
                    )}
                    <IconButton
                      aria-label={isMinimized ? 'expand' : 'minimize'}
                      color="inherit"
                      size="small"
                      onClick={() => handleMinimize(action.id)}
                    >
                      {isMinimized ? (
                        <ExpandMoreIcon fontSize="inherit" />
                      ) : (
                        <MinimizeIcon fontSize="inherit" sx={{ mb: 1 }} />
                      )}
                    </IconButton>
                    {action.dismissible && (
                      <IconButton
                        aria-label="close"
                        color="inherit"
                        size="small"
                        onClick={() => handleClose(action.id)}
                      >
                        <CloseIcon fontSize="inherit" />
                      </IconButton>
                    )}
                  </Box>
                }
                sx={{
                  mb: 0,
                  borderRadius: 2,
                  alignItems: isMinimized ? 'center' : 'flex-start',
                  ...action.style,
                }}
              >
                <AlertTitle sx={{ fontWeight: 'bold', mb: isMinimized ? 0 : 1 }}>
                  {action.title}
                </AlertTitle>
                <Collapse in={!isMinimized}>{action.message}</Collapse>
              </Alert>
            </Collapse>
          );
        })}
      </Stack>
    </Box>
  );
};

export default PriorityActionBanner;
